/**
 * Features page — `/features`.
 *
 * Every capability in detail, sourced from what the tool does today
 * (import parsers, plan model, toolbar, display options, export menu) and
 * the server tier table. Claims are validated facts only (framing V4):
 * the earlier "legacy desktop tools" comparison with unverifiable bullets
 * was removed at the claims review (task D.2).
 */

import Link from '../Link';
import { SCREENSHOT } from '../../lib/screenshot';
import { TIER_LIMIT } from '../../lib/tier';
import './FeaturesPage.css';

interface FeaturesPageProps {
  onSignup: () => void;
}

const DISPLAY_OPTION_LIST = [
  ['NB / SB Sequence', 'the progression band through every intersection at the design speed — the green wave itself'],
  ['NB / SB Next Green', 'departures that arrive on green at the next intersection only, for adjacent-pair coordination'],
  ['NB / SB Next', 'design-speed travel-path lines regardless of signal state'],
  ['NB / SB Phase Extents', 'dashed projections of each phase start (green) and end (red)'],
  ['Phase Letter, Phase Start Time', 'labels on the phase bars'],
] as const;

export default function FeaturesPage({ onSignup }: FeaturesPageProps) {
  const free = TIER_LIMIT.free;
  const pro = TIER_LIMIT.pro;

  return (
    <main className="features-page">
      <div className="features-container">
        <header className="features-hero features-hero-compact">
          <h1>Features</h1>
          <p className="features-hero-subtitle">
            What SigTDD does, in the order you use it: bring a corridor in, coordinate it, read the
            diagram, keep the plans, share and export.
          </p>
        </header>

        <section className="features-block" aria-labelledby="f-import">
          <h2 id="f-import">Import a corridor</h2>
          <p>
            Three formats are recognised automatically when you press <strong>Import</strong>:
          </p>
          <ul className="features-list">
            <li>
              <strong>CSV</strong> — one row per intersection: sequence number, name, distance to the next
              intersection in metres, northbound and southbound design speeds in km/h, offset in seconds,
              up to six phase durations (A to F) and an optional direction code per phase. Column names are
              matched loosely, so a spreadsheet export usually works first time.
            </li>
            <li>
              <strong>SigTDD JSON</strong> — a single timing plan, or the Corridor JSON that holds every
              timing plan for the corridor in one file.
            </li>
            <li>
              <strong>Synchro UTDF</strong> (Pro) — the Universal Traffic Data Format text export from Synchro.
              Signalised nodes, their coordinates, cycle lengths, offsets and phase timings are read and
              converted to a corridor. <Link to="/learn/synchro-utdf-import" event="learn-open" source="features">How the import maps UTDF fields</Link>.
            </li>
          </ul>
          <p>
            Six example corridors ship with the tool, so you can see a finished diagram before you enter
            a single number. New intersections can also be typed straight into the table.
          </p>
        </section>

        <section className="features-block" aria-labelledby="f-diagram">
          <h2 id="f-diagram">Read the time-distance diagram</h2>
          <p>
            Distance runs up the page, time runs across it. Each intersection is a bar of phases in
            sequence, shifted by its offset; the diagram redraws as soon as any value changes. The
            display options decide which projections you see:
          </p>
          <dl className="features-dl">
            {DISPLAY_OPTION_LIST.map(([term, text]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>{text}</dd>
              </div>
            ))}
          </dl>
          <p>
            Scroll to zoom, drag to pan, and use <strong>Fit</strong> to bring the whole corridor back into
            view. The corridor header shows length, cycle, speed range and the current bandwidth in each
            direction.
          </p>
          <figure className="features-figure">
            <img
              src={SCREENSHOT.pacificOriginal.src}
              width={SCREENSHOT.pacificOriginal.width}
              height={SCREENSHOT.pacificOriginal.height}
              alt={SCREENSHOT.pacificOriginal.alt}
              loading="lazy"
              decoding="async"
            />
            <figcaption>{SCREENSHOT.pacificOriginal.caption}</figcaption>
          </figure>
        </section>

        <section className="features-block" aria-labelledby="f-offset">
          <h2 id="f-offset">Set the offsets</h2>
          <p>
            Select an intersection and drag the offset slider under the diagram, or type the value in
            the table. <strong>Auto-sequence</strong> calculates every offset from spacing and design speed
            for northbound or southbound progression in one click; <strong>Smart</strong> mode (Pro) also
            weighs the opposing direction. Values are clamped to valid ranges as you type, and each phase
            can be assigned to northbound, southbound, both, or left out of the progression bands.
            <Link to="/learn/green-wave-offset" event="learn-open" source="features"> The offset arithmetic behind the button</Link>.
          </p>
        </section>

        <section className="features-block" aria-labelledby="f-plan">
          <h2 id="f-plan">Keep several timing plans per corridor</h2>
          <p>
            A corridor holds one set of geometry — names, distances, speeds — and up to{' '}
            {pro.maxPlansPerCorridor} timing plans that each carry their own offsets, phase durations and
            phase directions. Model the AM peak, the PM peak and the off-peak plan side by side, duplicate
            a plan to try a variation, and switch between them from the plan selector in the toolbar.
          </p>
          <figure className="features-figure">
            <img
              src={SCREENSHOT.harbourSbPriority.src}
              width={SCREENSHOT.harbourSbPriority.width}
              height={SCREENSHOT.harbourSbPriority.height}
              alt={SCREENSHOT.harbourSbPriority.alt}
              loading="lazy"
              decoding="async"
            />
            <figcaption>{SCREENSHOT.harbourSbPriority.caption}</figcaption>
          </figure>
        </section>

        <section className="features-block" aria-labelledby="f-share">
          <h2 id="f-share">Share and export</h2>
          <ul className="features-list">
            <li>
              <strong>Share link</strong> — a read-only view of a saved corridor that anyone can open in a
              browser without an account. Revoke it at any time.
            </li>
            <li>
              <strong>Copy Dark / Copy Light</strong> — the diagram to the clipboard for slides or a report,
              on a dark or a white background.
            </li>
            <li>
              <strong>CSV</strong> and <strong>JSON</strong> of the active plan, or <strong>Corridor JSON</strong> with
              every plan, for backup and for moving work between devices.
            </li>
            <li>
              <strong>PDF</strong> (Pro) — the diagram and the data table on one landscape page.
            </li>
          </ul>
          <p>
            Work in progress is kept in the browser automatically; saving to an account keeps it across
            devices and is what makes a corridor shareable.
          </p>
        </section>

        <section className="features-block" aria-labelledby="f-tier">
          <h2 id="f-tier">Free and Pro</h2>
          <table className="features-table">
            <thead>
              <tr>
                <th scope="col">Limit or capability</th>
                <th scope="col">Free</th>
                <th scope="col">Pro</th>
              </tr>
            </thead>
            <tbody>
              <tr><th scope="row">Saved corridors</th><td>{free.maxCorridors}</td><td>{pro.maxCorridors}</td></tr>
              <tr><th scope="row">Timing plans per corridor</th><td>{free.maxPlansPerCorridor}</td><td>{pro.maxPlansPerCorridor}</td></tr>
              <tr><th scope="row">Intersections per corridor</th><td>{free.maxIntersectionsPerCorridor}</td><td>{pro.maxIntersectionsPerCorridor}</td></tr>
              <tr><th scope="row">PDF export</th><td>{free.pdfExport ? 'Yes' : 'No'}</td><td>{pro.pdfExport ? 'Yes' : 'No'}</td></tr>
              <tr><th scope="row">Synchro UTDF import</th><td>{free.synchroImport ? 'Yes' : 'No'}</td><td>{pro.synchroImport ? 'Yes' : 'No'}</td></tr>
              <tr><th scope="row">Smart auto-sequence</th><td>{free.smartAutoSequence ? 'Yes' : 'No'}</td><td>{pro.smartAutoSequence ? 'Yes' : 'No'}</td></tr>
            </tbody>
          </table>
          <p>
            <Link to="/pricing">Pricing</Link> has the Pro price; the limits above are read from the same
            table the API enforces.
          </p>
        </section>

        <section className="features-block" aria-labelledby="f-origin">
          <h2 id="f-origin">Where it comes from</h2>
          <p>
            SigTDD is the browser rebuild of a Windows desktop time-distance diagram program first written
            in 2002 for corridor signal work. The drawing conventions — distance up the page, phase bars per
            intersection, projection lines at design speed — are the ones traffic engineers have used on
            paper and in desktop tools for decades; what changes is that there is nothing to install and the
            result can be shared with a link.
          </p>
        </section>

        <section className="features-final-cta">
          <h2>Try it on your corridor</h2>
          <p>Open the tool with an example loaded, or create a free account to save your own.</p>
          <div className="features-hero-cta">
            <Link to="/app" className="features-btn features-btn-secondary">Open the tool</Link>
            <button className="features-btn-primary" onClick={onSignup}>Create free account</button>
          </div>
          <p className="features-cta-note">No credit card required for the free tier.</p>
        </section>
      </div>
    </main>
  );
}
